import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import { useState, useMemo } from "react";

import React from "react";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddTopping from "../../Modals/addtopping";
import {
  useGetToppingListQuery,
  useDeleteToppingMutation,
  useUpdateToppingStatusMutation,
} from "../../store/api/commonAPI";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import ImageCarouselModal from "../../Modals/imageCarouselModal";
import { showToast, showError } from "../../constants/toasts";

const ManageToppings = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [openModal, setOpenModal] = useState(false);
  const [editTopping, setEditTopping] = useState<{
    id: string;
    name: string;
    image: string;
    sliceImage: string;
    quantityPerPiece: number;
    maxLimit: number;
    isActive: boolean;
    restricted: boolean;
  } | null>(null);

  // Fetch toppings from API
  const { data, isLoading, error, refetch } = useGetToppingListQuery({
    page,
    limit,
  });

  // Delete topping mutation
  const [deleteTopping, { isLoading: isDeleting }] = useDeleteToppingMutation();

  // Update topping status mutation
  const [updateToppingStatus, { isLoading: isUpdatingStatus }] =
    useUpdateToppingStatusMutation();

  // Extract toppings from API response
  const toppings = useMemo(() => {
    if (data?.data?.topping && Array.isArray(data.data.topping)) {
      return data.data.topping.map((topping: any) => ({
        id: topping._id || topping.id,
        name: topping.name || "N/A",
        image: topping.image || "/static/images/placeholder.png",
        sliceImage: topping.sliceImage || "/static/images/placeholder.png",
        quantityPerPiece: topping.quantityPerPiece || 0,
        maxLimit: topping.maxLimit || 0,
        isActive: topping.isActive ?? true,
        restricted: topping.restricted ?? false,
        createdAt: topping.createdAt
          ? new Date(topping.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "N/A",
      }));
    }
    return [];
  }, [data]);

  // Filter toppings based on search term
  const filteredToppings = useMemo(() => {
    if (!searchTerm) return toppings;
    return toppings.filter((topping) =>
      topping.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [toppings, searchTerm]);

  // Calculate total pages
  const totalCount = data?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditTopping(null);
    refetch();
  };

  // Handle edit button click
  const handleEditClick = (topping: {
    id: string;
    name: string;
    image: string;
    sliceImage: string;
    quantityPerPiece: number;
    maxLimit: number;
    isActive: boolean;
    restricted: boolean;
  }) => {
    setEditTopping(topping);
    setOpenModal(true);
  };

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [toppingToDelete, setToppingToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Image carousel modal state
  const [openImageModal, setOpenImageModal] = useState(false);
  const [selectedImages, setSelectedImages] = useState<string[]>([]);

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setToppingToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the topping
  const handleConfirmDelete = async () => {
    if (!toppingToDelete) return;

    try {
      const response = await deleteTopping({ id: toppingToDelete.id }).unwrap();

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Topping "${toppingToDelete.name}" deleted successfully`;
      showToast(successMessage);

      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setToppingToDelete(null);
    } catch (error: any) {
      console.error("Error deleting topping:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete topping. Please try again.";

      showError(errorMessage);
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setToppingToDelete(null);
    }
  };

  // Handle status toggle (isActive)
  const handleStatusToggle = async (
    toppingId: string,
    toppingName: string,
    currentStatus: boolean,
    currentRestricted: boolean
  ) => {
    const newStatus = !currentStatus;
    try {
      const response = await updateToppingStatus({
        id: toppingId,
        isActive: newStatus,
        restricted: currentRestricted,
      }).unwrap();
      const successMessage =
        response?.message ||
        `Topping "${toppingName}" ${
          newStatus ? "activated" : "deactivated"
        } successfully`;
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating topping status:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update topping status. Please try again.";
      showError(errorMessage);
      refetch(); // Revert UI state if update failed
    }
  };

  // Handle restrict redemption toggle
  const handleRestrictToggle = async (
    toppingId: string,
    toppingName: string,
    currentStatus: boolean,
    currentRestricted: boolean
  ) => {
    const newRestricted = !currentRestricted;
    try {
      const response = await updateToppingStatus({
        id: toppingId,
        isActive: currentStatus,
        restricted: newRestricted,
      }).unwrap();
      const successMessage =
        response?.message ||
        `Topping "${toppingName}" redemption ${
          newRestricted ? "restricted" : "unrestricted"
        } successfully`;
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating topping restriction:", error);
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update topping restriction. Please try again.";
      showError(errorMessage);
      refetch(); // Revert UI state if update failed
    }
  };

  // Handle opening image modal
  const handleOpenImageModal = (images: any[]) => {
    // Process images to get proper URLs
    const processedImages = images.map((img) => {
      let imageUrl: string;
      
      if (typeof img === "object" && img !== null && "image" in img) {
        imageUrl = (img as any).image;
      } else if (typeof img === "string") {
        imageUrl = img;
      } else {
        imageUrl = "/static/images/placeholder.png";
      }

      // Format URL if needed
      if (!imageUrl) {
        return "/static/images/placeholder.png";
      }
      
      if (imageUrl.startsWith("http") || imageUrl.startsWith("/static/")) {
        return imageUrl;
      }
      
      return `/static/images/${imageUrl}`;
    });
    setSelectedImages(processedImages);
    setOpenImageModal(true);
  };

  // Handle closing image modal
  const handleCloseImageModal = () => {
    setOpenImageModal(false);
    setSelectedImages([]);
  };

  return (
    <>
      <Loader isLoad={isLoading || isDeleting || isUpdatingStatus} />
      <div className="main_title">
        <h1>Manage Toppings</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Toppings
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Toppings..."
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setEditTopping(null);
                navigate("/manage-toppings/add");
              }}
            >
              <AddIcon /> Add Topping
            </Button>
          </div>
        </div>

        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No</TableCell>
                <TableCell>Topping</TableCell>
                <TableCell>Topping Slice</TableCell>
                <TableCell>Quantity</TableCell>
                <TableCell>Max Limit</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Restrict Redemption</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Error loading toppings. Please try again.
                  </TableCell>
                </TableRow>
              ) : filteredToppings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    {isLoading ? "Loading..." : "No toppings found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredToppings.map((topping: any, index: number) => (
                  <TableRow hover key={topping.id}>
                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                    <TableCell>
                      <Box component="a" className="user_block">
                        <figure>
                          <img
                            src={
                              topping.image.startsWith("http")
                                ? topping.image
                                : `/static/images/${topping.image}`
                            }
                            alt={topping.name}
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "/static/images/placeholder.png";
                            }}
                          />
                        </figure>
                        <p>
                          <b>{topping.name}</b>
                        </p>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        {Array.isArray(topping.sliceImage) &&
                        topping.sliceImage.length > 0 ? (
                          <>
                            {topping.sliceImage.slice(0, 2).map(
                              (img: string | object, index: number) => {
                                let imageUrl: string;
                                
                                if (typeof img === "object" && img !== null && "image" in img) {
                                  imageUrl = (img as any).image || "/static/images/placeholder.png";
                                } else if (typeof img === "string") {
                                  imageUrl = img || "/static/images/placeholder.png";
                                } else {
                                  imageUrl = "/static/images/placeholder.png";
                                }
                                
                                const formattedUrl = 
                                  imageUrl.startsWith("http") || imageUrl.startsWith("/static/")
                                    ? imageUrl
                                    : `/static/images/${imageUrl}`;
                                
                                return (
                                  <Box
                                    key={index}
                                    component="figure"
                                    onClick={() => handleOpenImageModal(topping.sliceImage)}
                                    sx={{
                                      width: 50,
                                      height: 50,
                                      borderRadius: 1,
                                      overflow: "hidden",
                                      flexShrink: 0,
                                      cursor: "pointer",
                                      transition: "transform 0.2s",
                                      "&:hover": {
                                        transform: "scale(1.05)",
                                      },
                                    }}
                                  >
                                    <img
                                      src={formattedUrl}
                                      alt={`Slice ${index + 1}`}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                      onError={(e) => {
                                        (e.target as HTMLImageElement).src =
                                          "/static/images/placeholder.png";
                                      }}
                                    />
                                  </Box>
                                );
                              }
                            )}
                            {topping.sliceImage.length > 2 && (
                              <Box
                                onClick={() => handleOpenImageModal(topping.sliceImage)}
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#f5f5f5",
                                  border: "1px solid #e0e0e0",
                                  cursor: "pointer",
                                  transition: "background-color 0.2s",
                                  "&:hover": {
                                    backgroundColor: "#e0e0e0",
                                  },
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  color: "#666",
                                }}
                              >
                                +{topping.sliceImage.length - 2}
                              </Box>
                            )}
                          </>
                        ) : (
                          <Box
                            component="figure"
                            sx={{
                              width: 50,
                              height: 50,
                              borderRadius: 1,
                              overflow: "hidden",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: "#f5f5f5",
                            }}
                          >
                            <img
                              src="/static/images/placeholder.png"
                              alt="No slice"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      {topping.quantityPerPiece}{" "}
                      {topping.quantityPerPiece === 1 ? "piece" : "pieces"}
                    </TableCell>
                    <TableCell>{topping.maxLimit}</TableCell>
                    <TableCell>{topping.createdAt}</TableCell>
                    <TableCell>
                      <SwitchToggle
                        checked={topping.restricted}
                        onChange={() =>
                          topping.id &&
                          handleRestrictToggle(
                            topping.id,
                            topping.name,
                            topping.isActive,
                            topping.restricted
                          )
                        }
                        disabled={isUpdatingStatus || !topping.id}
                      />
                    </TableCell>
                    <TableCell>
                      <SwitchToggle
                        checked={topping.isActive}
                        onChange={() =>
                          topping.id &&
                          handleStatusToggle(
                            topping.id,
                            topping.name,
                            topping.isActive,
                            topping.restricted
                          )
                        }
                        disabled={isUpdatingStatus || !topping.id}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => {
                            navigate(`/manage-toppings/edit/${topping.id}`, {
                              state: { topping },
                            });
                            // if (topping.id) {
                            //   handleEditClick({
                            //     id: topping.id,
                            //     name: topping.name,
                            //     image: topping.image,
                            //     sliceImage: topping.sliceImage,
                            //     quantityPerPiece: topping.quantityPerPiece,
                            //     maxLimit: topping.maxLimit,
                            //     isActive: topping.isActive,
                            //     restricted: topping.restricted,
                            //   });
                            // }
                          }}
                          disabled={!topping.id}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            topping.id &&
                            handleDeleteClick(topping.id, topping.name)
                          }
                          disabled={isDeleting || !topping.id}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 0 && (
          <div
            className="table_footer"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "15px 20px",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <p style={{ margin: 0, color: "#717171", fontSize: "14px" }}>
              Showing <strong>{(page - 1) * limit + 1}</strong> to{" "}
              <strong>{Math.min(page * limit, totalCount)}</strong> of{" "}
              <strong>{totalCount}</strong> entries
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Pagination
                module={filteredToppings}
                page={page}
                onPageChange={handlePageChange}
                totalPages={totalPages}
                setPage={setPage}
                totalCount={totalCount}
                limit={limit}
              />
            </div>
          </div>
        )}
      </div>

      {/* <AddTopping
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        editTopping={
          editTopping
            ? {
                _id: editTopping.id,
                name: editTopping.name,
                image: editTopping.image,
                sliceImage: editTopping.sliceImage,
                quantityPerPiece: editTopping.quantityPerPiece,
                maxLimit: editTopping.maxLimit,
                isActive: editTopping.isActive,
                restricted: editTopping.restricted,
              }
            : null
        }
      /> */}
      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={toppingToDelete?.name}
        isLoading={isDeleting}
      />

      {/* Image Carousel Modal */}
      <ImageCarouselModal
        open={openImageModal}
        onClose={handleCloseImageModal}
        setOpen={setOpenImageModal}
        images={selectedImages}
        title="Topping Slice Images"
      />
    </>
  );
};

export default ManageToppings;
